import { queryClient } from "src/lib/react-query";
import { hraConfigMgmtApi } from "./hris-rep";
import { queryOptions, useMutation } from "@tanstack/react-query";
import { TerminationWorkflow } from "src/apps/hr-config-management/types/TerminationWorkflow";

function hrOrgRepQueryOptions() {
    return queryOptions({
        queryKey: [
            "hr-org-rep"
        ],
        queryFn: () => hraConfigMgmtApi.getOrgRepresentativeRaw(),
        retry: false,
        staleTime: 0,
        gcTime: 0,
    });
}

function hrWorksiteRepQueryOptions() {
    return queryOptions({
        queryKey: [
            "hr-worksite-rep"
        ],
        queryFn: () => hraConfigMgmtApi.getWorksiteRepresentativeRaw(),
        retry: false,
        staleTime: 0,
        gcTime: 0,
    });
}

function hrAssigneeQueryOptions() {
    return queryOptions({
        queryKey: [
            "hr-assignees"
        ],
        queryFn: () => hraConfigMgmtApi.getHRAssignee(),
        retry: false,
        staleTime: 0,
        gcTime: 0,
    });
}

function yellowMarkedQueryOptions() {
    return queryOptions({
        queryKey: [
            "yellow-marked"
        ],
        queryFn: () => hraConfigMgmtApi.getYellow(),
        retry: false,
        staleTime: 0,
        gcTime: 0,
    });
}

function yellowMarkedApplicantQueryOptions(data: { params: {ApplicantUID: string}}) {
    return queryOptions({
        queryKey: [
            "yellow-marked-applicant",
            {ApplicantUID: data.params.ApplicantUID}
        ],
        queryFn: () => hraConfigMgmtApi.getYellowApplicant(data),
        retry: false,
        staleTime: 0,
        gcTime: 0,
    });
}

function yellowMarkedTenstreetApplicantQueryOptions(data: { params: {StageRefID: number}}) {
    return queryOptions({
        queryKey: [
            "yellow-marked-applicant-tenstreet",
            {StageRefID: data.params.StageRefID}
        ],
        queryFn: () => hraConfigMgmtApi.getYellowTenstreetApplicant(data),
        retry: false,
        staleTime: 0,
        gcTime: 0,
    });
}

function yellowMarkedJobMasterQueryOptions(data: { params: {EmployeeUid: string}}) {
    return queryOptions({
        queryKey: [
            "yellow-marked-job-master",
            {EmployeeUid: data.params.EmployeeUid}
        ],
        queryFn: () => hraConfigMgmtApi.getYellowJobMaster(data),
        retry: false,
        staleTime: 0,
        gcTime: 0,
    });
}

function termWorkflowQueryOptions(data: { params: {WorkflowRequestLogID: number}}) {
    return queryOptions({
        queryKey: [
            "termination-workflow",
            {WorkflowRequestLogID: data.params.WorkflowRequestLogID}
        ],
        queryFn: () => hraConfigMgmtApi.getTerminationWorkflow(data),
        select: (response: TerminationWorkflow[]) => response?.[0],
        retry: false,
        staleTime: 0,
        gcTime: 0,
    });
}

function termReasonQueryOptions() {
    return queryOptions({
        queryKey: [
            "termination-reasons",
        ],
        queryFn: () => hraConfigMgmtApi.getTerminationReasons(),
        retry: false,
        staleTime: 0,
        gcTime: 0,
    });
}

function termCategoryQueryOptions(data: { params: {EmployeeType: string | undefined}}) {
    return queryOptions({
        queryKey: [
            "termination-categories",
            {EmployeeUid: data.params.EmployeeType}
        ],
        queryFn: () => hraConfigMgmtApi.getTerminationCategories(data),
        retry: false,
        staleTime: 0,
        gcTime: 0,
    });
}

function getWorksiteQueryOptions() {
    return queryOptions({
        queryKey: [
            "worksite-ids",
        ],
        queryFn: () => hraConfigMgmtApi.getWorksites(),
        retry: false,
        staleTime: 0,
        gcTime: 0,
    });
}

function getOrgWorksiteRelationshipQueryOptions() {
    return queryOptions({
        queryKey: [
            "worksite-org-relationship",
        ],
        queryFn: () => hraConfigMgmtApi.getOrgWorksiteRelatinoships(),
        retry: false,
        staleTime: 0,
        gcTime: 0,
    });
}

function useUpdateHRAssignments() {
    return useMutation({
        mutationKey: ["hr-org-rep"],
        mutationFn: hraConfigMgmtApi.updateOrgRepresentatives,
        onSuccess: () => 
        {
            queryClient.invalidateQueries({
                queryKey: hrisConfigQueryOptions.hrOrgRepQueryOptions().queryKey
            })
        }
    });
}

function useUpdateWorksiteHRAssignments() {
    return useMutation({
        mutationKey: ["hr-worksite-rep"],
        mutationFn: hraConfigMgmtApi.updateWorksiteRepresentatives,
        onSuccess: () => 
        {
            queryClient.invalidateQueries({
                queryKey: hrisConfigQueryOptions.hrWorksiteRepQueryOptions().queryKey
            })
        }
    });
}

function hrDriverSupervisorMapQueryOptions() {
    return queryOptions({
        queryKey: [
            "engauge-supervisor-map"
        ],
        queryFn: () => hraConfigMgmtApi.getDriverSupervisorMap(),
        retry: false,
        staleTime: 0,
        gcTime: 0,
    });
}

function hrSupervisorQueryOptions() {
    return queryOptions({
        queryKey: [
            "engauge-supervisors"
        ],
        queryFn: () => hraConfigMgmtApi.getSupervisors(),
        retry: false,
        staleTime: 0,
        gcTime: 0,
    });
}

function useUpdateDriverSupervisorMap() {
    return useMutation({
        mutationKey: ["engauge-supervisor-mod"],
        mutationFn: hraConfigMgmtApi.updateFleetDriverSupervisor,
        onSuccess: () => 
        {
            queryClient.invalidateQueries({
                queryKey: hrisConfigQueryOptions.hrDriverSupervisorMapQueryOptions().queryKey
            })
        }
    });
}

function useCreateDriverSupervisorMap() {
    return useMutation({
        mutationKey: ["engauge-fleet-supervisor"],
        mutationFn: hraConfigMgmtApi.createFleetDriverSupervisor,
        onSuccess: () => 
        {
            queryClient.invalidateQueries({
                queryKey: hrisConfigQueryOptions.hrDriverSupervisorMapQueryOptions().queryKey
            })
        }
    });
}

function useUpdateLastWorkDate() {
    return useMutation({
        mutationKey: ["last-work-date"],
        mutationFn: hraConfigMgmtApi.updateLastWorkDate,
    });
}

function useUpdateTerminationReason() {
    return useMutation({
        mutationKey: ["term-reason"],
        mutationFn: hraConfigMgmtApi.updateTerminationReason,
    });
}

function useUpdateTerminationCategory() {
    return useMutation({
        mutationKey: ["term-category"],
        mutationFn: hraConfigMgmtApi.updateTerminationCategory,
    });
}

function useCreateWorksite() {
    return useMutation({
        mutationKey: ["hra-worksite"],
        mutationFn: hraConfigMgmtApi.createWorksite,
    });
}

function useCreateOrgWorksiteRelationship() {
    return useMutation({
        mutationKey: ["hra-worksite-relationhip"],
        mutationFn: hraConfigMgmtApi.createWorksiteOrganizationRelationship,
    });
}

function useUpdateTerminationNotes() {
    return useMutation({
        mutationKey: ["term-notes"],
        mutationFn: hraConfigMgmtApi.updateTerminationNotes,
    });
}

const hrisConfigQueryOptions =
{
    hrAssigneeQueryOptions,
    hrOrgRepQueryOptions,
    hrWorksiteRepQueryOptions,
    hrSupervisorQueryOptions,
    hrDriverSupervisorMapQueryOptions,
    yellowMarkedQueryOptions,
    yellowMarkedJobMasterQueryOptions,
    yellowMarkedTenstreetApplicantQueryOptions,
    yellowMarkedApplicantQueryOptions,
    termWorkflowQueryOptions,
    termReasonQueryOptions,
    termCategoryQueryOptions,
    getWorksiteQueryOptions,
    getOrgWorksiteRelationshipQueryOptions,
}

const hrisConfigMutations = 
{
    useUpdateHRAssignments,
    useUpdateWorksiteHRAssignments,
    useCreateDriverSupervisorMap,
    useUpdateDriverSupervisorMap,
    useUpdateLastWorkDate,
    useUpdateTerminationCategory,
    useUpdateTerminationReason,
    useUpdateTerminationNotes,
    useCreateWorksite,
    useCreateOrgWorksiteRelationship,
}

export
{
    hrisConfigQueryOptions,
    hrisConfigMutations
}